import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Pill } from "@/components/podkit/status/Pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import type { PlainEnvironment } from "@/queries/environment-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type EnvironmentSpec_Secret } from "gitpod-next-api/gitpod/v1/environment_pb";
import { type Secret } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useMemo, type FC } from "react";

type Props = {
    environment: PlainEnvironment;
    userSecrets: PlainMessage<Secret>[];
    organizationSecrets: PlainMessage<Secret>[];
};

export const EnvironmentSecretsTable: FC<Props> = ({ environment, userSecrets, organizationSecrets }) => {
    // Get secrets with warnings and their messages
    const secretsWithWarnings = useMemo(() => {
        const warningSecrets: { secret: PlainMessage<EnvironmentSpec_Secret>; warningMessage: string }[] = [];
        const secrets = environment.status?.secrets ?? [];

        for (const secret of secrets) {
            if (secret.warningMessage) {
                // Find the corresponding spec secret
                const specSecret = environment.spec?.secrets?.find((s) => s.name === secret.secretName);
                if (specSecret) {
                    warningSecrets.push({ secret: specSecret, warningMessage: secret.warningMessage });
                }
            }
        }

        return warningSecrets;
    }, [environment.status?.secrets, environment.spec?.secrets]);

    const secretsBySource = useMemo(() => {
        const result = {
            organization: [] as PlainMessage<EnvironmentSpec_Secret>[],
            project: [] as PlainMessage<EnvironmentSpec_Secret>[],
            user: [] as PlainMessage<EnvironmentSpec_Secret>[],
            environment: [] as PlainMessage<EnvironmentSpec_Secret>[],
            overriden: new Set<string>(),
        };

        const statusIdMap = new Map<string, PlainMessage<EnvironmentSpec_Secret>>();
        for (const secret of environment.spec?.secrets || []) {
            // Skip secrets that have warnings - they'll be shown as warning rows
            if (secretsWithWarnings.some((w) => w.secret.name === secret.name)) {
                continue;
            }

            // Determine statusID for shadowing detection (same logic as in secret reconciler in supervisor)
            let statusID = secret.name;
            switch (secret.mount.case) {
                case "filePath":
                    statusID = `file:${secret.mount.value}`;
                    break;
                case "environmentVariable":
                    statusID = `env:${secret.mount.value}`;
                    break;
                case "gitCredentialHost":
                    statusID = `git:${secret.mount.value}`;
                    break;
                case "containerRegistryBasicAuthHost":
                    statusID = `registry:${secret.mount.value}`;
                    break;
            }

            if (statusIdMap.has(statusID)) {
                const prevSecret = statusIdMap.get(statusID)!;
                result.overriden.add(prevSecret.id);
            }
            statusIdMap.set(statusID, secret);

            // Categorize secret by source
            // Secrets without a sourceRef are environment-level secrets (e.g., ENVIRONMENT_TOKEN, SCM_TOKEN)
            // injected by the runner, not stored in the control-plane
            if (!secret.sourceRef) {
                result.environment.push(secret);
            } else if (userSecrets.some((s) => s.id === secret.sourceRef)) {
                result.user.push(secret);
            } else if (organizationSecrets.some((s) => s.id === secret.sourceRef)) {
                result.organization.push(secret);
            } else {
                result.project.push(secret);
            }
        }

        return result;
    }, [environment.spec?.secrets, userSecrets, organizationSecrets, secretsWithWarnings]);

    const specSecretsCount =
        secretsBySource.organization.length +
        secretsBySource.project.length +
        secretsBySource.user.length +
        secretsBySource.environment.length;

    if (!specSecretsCount) {
        return (
            <div data-testid="environment-secrets-empty-state">
                <Text className="text-base text-content-tertiary">This environment has no secrets.</Text>
            </div>
        );
    }

    return (
        <Table className="table-fixed" data-testid="environment-secrets-table">
            <TableHeader className="h-10 [&_th:last-child]:pl-0 [&_th:last-child]:pr-2 [&_th]:border-border-base [&_th]:border-b-[0.5] [&_th]:bg-transparent [&_th]:px-2 [&_th]:py-1">
                <TableRow>
                    <TableHead className="w-[200px] text-sm font-bold text-content-secondary">Name</TableHead>
                    <TableHead />
                    <TableHead className="w-[120px] text-right text-sm font-bold text-content-secondary">
                        Source
                    </TableHead>
                    <TableHead className="w-[34px]" />
                </TableRow>
            </TableHeader>
            <TableBody>
                {secretsBySource.organization.map((secret, i) => (
                    <EnvironmentSecretsRow
                        key={`secret-${secret.session}-${i}`}
                        secret={secret}
                        secretScope="organization"
                        overriden={secretsBySource.overriden.has(secret.id)}
                    />
                ))}
                {secretsBySource.project.map((secret, i) => (
                    <EnvironmentSecretsRow
                        key={`secret-${secret.session}-${i}`}
                        secret={secret}
                        secretScope="project"
                        overriden={secretsBySource.overriden.has(secret.id)}
                    />
                ))}
                {secretsBySource.user.map((secret, i) => (
                    <EnvironmentSecretsRow
                        key={`secret-${secret.session}-${i}`}
                        secret={secret}
                        secretScope="user"
                        overriden={secretsBySource.overriden.has(secret.id)}
                    />
                ))}
                {secretsBySource.environment.map((secret, i) => (
                    <EnvironmentSecretsRow
                        key={`secret-${secret.session}-${i}`}
                        secret={secret}
                        secretScope="environment"
                        overriden={secretsBySource.overriden.has(secret.id)}
                    />
                ))}
                {secretsWithWarnings.map(({ secret, warningMessage }, i) => (
                    <EnvironmentSecretsWarningRow
                        key={`warning-secret-${secret.session}-${i}`}
                        secret={secret}
                        warningMessage={warningMessage}
                        userSecrets={userSecrets}
                        organizationSecrets={organizationSecrets}
                    />
                ))}
            </TableBody>
        </Table>
    );
};

const EnvironmentSecretsRow: FC<{
    secret: PlainMessage<EnvironmentSpec_Secret>;
    secretScope: "organization" | "project" | "user" | "environment";
    overriden?: boolean;
}> = ({ secret, secretScope, overriden }) => {
    const { toast } = useToast();
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyToClipboard = useCallback(() => {
        let copyValue = "";
        if (secret.mount.case === "apiOnly") {
            copyValue = secret.name;
        } else if (typeof secret.mount.value === "string") {
            copyValue = secret.mount.value;
        }
        void navigator.clipboard.writeText(copyValue).then(() => {
            setCopied(true);
            toast({ title: "Copied to clipboard" });
        });
    }, [secret.mount.value, secret.mount.case, secret.name, setCopied, toast]);

    let mountLabel = "Environment variable";
    let tooltip = "Copy name";
    if (secret.mount.case === "filePath" && typeof secret.mount.value === "string") {
        mountLabel = secret.mount.value;
        tooltip = "Copy path";
    } else if (secret.mount.case === "containerRegistryBasicAuthHost" && typeof secret.mount.value === "string") {
        mountLabel = `Registry secret (hostname: ${secret.mount.value})`;
        tooltip = "Copy registry hostname";
    } else if (secret.mount.case === "gitCredentialHost" && typeof secret.mount.value === "string") {
        mountLabel = `Git credential (host: ${secret.mount.value})`;
        tooltip = "Copy git host";
    } else if (secret.mount.case === "apiOnly") {
        mountLabel = "API only";
        tooltip = "Copy name";
    }

    return (
        <TableRow
            className="group border-0 hover:bg-surface-02 [&_td:first-child]:rounded-l-md [&_td:first-child]:pl-2 [&_td:last-child]:rounded-r-md [&_td:last-child]:pl-0 [&_td:last-child]:pr-2 [&_td]:px-2 [&_td]:py-1"
            data-testid={`secret-${secret.name}`}
        >
            <TableCell className="p-0">
                <Tooltip content={secret.name}>
                    <Text
                        className={cn("truncate font-mono text-sm font-bold text-content-primary", {
                            "line-through": overriden,
                        })}
                    >
                        {secret.name}
                    </Text>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Tooltip content={mountLabel}>
                    <Text
                        className={cn("truncate text-sm text-content-secondary", {
                            "line-through": overriden,
                        })}
                    >
                        {mountLabel}
                    </Text>
                </Tooltip>
            </TableCell>
            <TableCell className="text-right">
                <SecretScopePill secretScope={secretScope} />
            </TableCell>
            <TableCell>
                <Tooltip content={tooltip}>
                    <Button
                        variant="link"
                        size="sm"
                        disabled={copied}
                        LeadingIcon={
                            copied
                                ? (p) => <IconCheck className="text-green-500 hover:text-green-500" {...p} />
                                : IconCopy
                        }
                        className="hidden h-full min-h-0 border-0 p-0 active:outline-0 group-hover:flex"
                        onClick={handleCopyToClipboard}
                        aria-label={tooltip}
                        data-tracking-id="copy-secret-value-environment-secrets-row"
                    />
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

const EnvironmentSecretsWarningRow: FC<{
    secret: PlainMessage<EnvironmentSpec_Secret>;
    warningMessage: string;
    userSecrets: PlainMessage<Secret>[];
    organizationSecrets: PlainMessage<Secret>[];
}> = ({ secret, warningMessage, userSecrets, organizationSecrets }) => {
    const { toast } = useToast();
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyToClipboard = useCallback(() => {
        let copyValue = "";
        if (secret.mount.case === "apiOnly") {
            copyValue = secret.name;
        } else if (typeof secret.mount.value === "string") {
            copyValue = secret.mount.value;
        }
        void navigator.clipboard.writeText(copyValue).then(() => {
            setCopied(true);
            toast({ title: "Copied to clipboard" });
        });
    }, [secret.mount.value, secret.mount.case, secret.name, setCopied, toast]);

    // Determine secret scope based on sourceRef (same logic as in main component)
    let secretScope: "organization" | "project" | "user" | "environment" = "project";
    if (!secret.sourceRef) {
        secretScope = "environment";
    } else if (userSecrets.some((s) => s.id === secret.sourceRef)) {
        secretScope = "user";
    } else if (organizationSecrets.some((s) => s.id === secret.sourceRef)) {
        secretScope = "organization";
    }

    let mountLabel = "Environment variable";
    let tooltip = "Copy name";
    if (secret.mount.case === "filePath" && typeof secret.mount.value === "string") {
        mountLabel = secret.mount.value;
        tooltip = "Copy path";
    } else if (secret.mount.case === "containerRegistryBasicAuthHost" && typeof secret.mount.value === "string") {
        mountLabel = `Registry secret (hostname: ${secret.mount.value})`;
        tooltip = "Copy registry hostname";
    } else if (secret.mount.case === "gitCredentialHost" && typeof secret.mount.value === "string") {
        mountLabel = `Git credential (host: ${secret.mount.value})`;
        tooltip = "Copy git host";
    } else if (secret.mount.case === "apiOnly") {
        mountLabel = "API only";
        tooltip = "Copy name";
    }

    return (
        <TableRow
            className="group border-0 hover:bg-surface-02 [&_td:first-child]:rounded-l-md [&_td:first-child]:pl-2 [&_td:last-child]:rounded-r-md [&_td:last-child]:pl-0 [&_td:last-child]:pr-2 [&_td]:px-2 [&_td]:py-1"
            data-testid={`warning-secret-${secret.name}`}
        >
            <TableCell className="p-0">
                <div className="flex items-center gap-2">
                    <Tooltip content={warningMessage} usePortal={true}>
                        <svg className="h-4 w-4 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                            <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </Tooltip>
                    <Tooltip content={secret.name}>
                        <Text className="truncate font-mono text-sm font-bold text-content-primary">{secret.name}</Text>
                    </Tooltip>
                </div>
            </TableCell>
            <TableCell>
                <Tooltip content={mountLabel}>
                    <Text className="truncate text-sm text-content-secondary">{mountLabel}</Text>
                </Tooltip>
            </TableCell>
            <TableCell className="text-right">
                <SecretScopePill secretScope={secretScope} />
            </TableCell>
            <TableCell>
                <Tooltip content={tooltip}>
                    <Button
                        variant="link"
                        size="sm"
                        disabled={copied}
                        LeadingIcon={
                            copied
                                ? (p) => <IconCheck className="text-green-500 hover:text-green-500" {...p} />
                                : IconCopy
                        }
                        className="hidden border-0 p-0 active:outline-0 group-hover:flex"
                        onClick={handleCopyToClipboard}
                        aria-label={tooltip}
                        data-tracking-id="copy-secret-value-environment-secrets-warning-row"
                    />
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

const SecretScopePill: FC<{ secretScope: "organization" | "user" | "project" | "environment" }> = ({ secretScope }) => {
    const label = {
        organization: "Organization",
        project: "Project",
        user: "User",
        environment: "Environment",
    }[secretScope];

    return (
        <Pill variant={"neutral"} size={"sm"} className="whitespace-nowrap">
            {label}
        </Pill>
    );
};
